import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';

import CommonSelect from '../../../form-components/CommonSelect';
import CommonPagination from '../../../form-components/CommonPagination';
import CommonDeleteModal from '../../../form-components/CommonDeleteModal';
import { stripHtml } from '../../../utils/methods';
import {
  deleteContractTemplate,
  getContractTemplatePdf,
  getContractTemplates,
  toggleContractTemplateActive,
} from '../../../api/contract';
import { getContractCategories } from '../../../api/contract';
import LogoSealModal from './logoSealModal/LogoSealModal';
import {
  ToolbarRow,
  PageSizeGroup,
  PageSizeLabel,
  SearchField,
  SearchInput,
  ActionGroup,
  ActionButton,
  Table,
  TableHeader,
  TableRow,
  Cell,
  HeaderText,
  CellDivider,
  CheckBoxCell,
  DateText,
  NameText,
  ContentText,
  CategoryText,
  RegisterText,
  UsageToggle,
  RowActionGroup,
  EditButton,
  DeleteButton,
} from './ElectronicContractStyled';

// 표 헤더 정의 — width 가 있으면 고정폭, 없으면 flex:1 (Figma 비율)
const COLUMNS = [
  { key: 'createdAt', label: '계약서 생성일', width: 160 },
  { key: 'name', label: '계약서 명', width: 400 },
  { key: 'content', label: '계약서 내용', width: 480 },
  { key: 'category', label: '카테고리' },
  { key: 'usage', label: '사용여부' },
  { key: 'action', label: '수정 및 삭제' },
];

const PAGE_SIZE_OPTIONS = [
  { value: 10, label: '10' },
  { value: 20, label: '20' },
  { value: 50, label: '50' },
];

// "2026-06-04 10:30:00" → "2026-06-04"
const toDate = value => (value ? value.slice(0, 10) : '');

const ElectronicContractManage = ({ branchId, logoSeal, onCreate, onEdit }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const token = useSelector(state => state.user.user?.token);

  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [appliedKeyword, setAppliedKeyword] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [isLogoSealModalOpen, setIsLogoSealModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const buildQuery = () => {
    // 백엔드 SearchRequest(PageableDTO.Request 상속): keyword/branchId/page/limit/sortOrder
    const params = new URLSearchParams({ page, limit: pageSize, sortOrder: 'DESCENDING' });
    if (branchId) params.set('branchId', branchId);
    if (appliedKeyword) params.set('keyword', appliedKeyword);
    return params.toString();
  };

  const { data } = useQuery({
    queryKey: ['contractTemplates', branchId, appliedKeyword, page, pageSize],
    queryFn: () => getContractTemplates(token, buildQuery()),
    enabled: !!token && !!branchId,
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['contractCategories'],
    queryFn: () => getContractCategories(token),
    enabled: !!token,
    staleTime: 1000 * 60 * 30,
  });

  const rows = data?.content || [];

  const toggleMutation = useMutation({
    mutationFn: id => toggleContractTemplateActive(id, token),
    onSuccess: () => {
      // 같은 카테고리의 기존 active 가 자동 OFF 되므로 목록 전체를 갱신한다.
      queryClient.invalidateQueries({ queryKey: ['contractTemplates'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: id => deleteContractTemplate(id, token),
    onSuccess: () => {
      toast.success(t('성공적으로 삭제되었습니다'));
      setDeleteTarget(null);
      setSelectedIds(prev => prev.filter(selectedId => selectedId !== deleteTarget?.id));
      queryClient.invalidateQueries({ queryKey: ['contractTemplates'] });
    },
  });

  const applySearch = () => {
    setPage(1);
    setAppliedKeyword(keyword.trim());
  };

  const handlePageSizeChange = e => {
    setPageSize(e.target.value);
    setPage(1);
  };

  const allChecked = rows.length > 0 && rows.every(row => selectedIds.includes(row.id));
  const toggleSelectAll = () => setSelectedIds(allChecked ? [] : rows.map(row => row.id));
  const toggleSelectOne = id =>
    setSelectedIds(prev => (prev.includes(id) ? prev.filter(selectedId => selectedId !== id) : [...prev, id]));

  // 선택된 계약서들의 PDF 를 순회하며 각각 다운로드한다.
  const handleDownloadPdf = async () => {
    if (selectedIds.length === 0) return toast.error(t('PDF를 다운로드할 계약서를 선택해 주세요.'));
    for (const id of selectedIds) {
      try {
        const blob = await getContractTemplatePdf(id, token);
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `contract-${id}.pdf`;
        anchor.click();
        window.URL.revokeObjectURL(url);
      } catch {
        toast.error(t('PDF 다운로드에 실패했습니다.'));
      }
    }
  };

  return (
    <>
      {/* 검색 / 액션 영역 */}
      <ToolbarRow>
        <PageSizeGroup>
          <PageSizeLabel>{t('페이지당 항목 수')}</PageSizeLabel>
          <CommonSelect value={pageSize} onChange={handlePageSizeChange} options={PAGE_SIZE_OPTIONS} width={100} />
        </PageSizeGroup>

        <SearchField>
          <SearchInput
            placeholder={t('계약서 명 및 내용으로 검색')}
            value={keyword}
            onChange={e => setKeyword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && applySearch()}
          />
          <SearchIcon sx={{ color: '#A6A6A6', cursor: 'pointer' }} onClick={applySearch} />
        </SearchField>

        <ActionGroup>
          <ActionButton
            tone="outline"
            startIcon={<PictureAsPdfOutlinedIcon />}
            onClick={() => setIsLogoSealModalOpen(true)}>
            {t('로고&직인 등록')}
          </ActionButton>
          <ActionButton tone="danger" startIcon={<PictureAsPdfOutlinedIcon />} onClick={handleDownloadPdf}>
            {t('PDF 다운로드')}
          </ActionButton>
          <ActionButton tone="primary" startIcon={<AddIcon />} onClick={onCreate}>
            {t('계약서 만들기')}
          </ActionButton>
        </ActionGroup>
      </ToolbarRow>

      {/* 표 */}
      <Table>
        <TableHeader>
          <CheckBoxCell checked={allChecked} clickable onClick={toggleSelectAll}>
            {allChecked && <CheckIcon sx={{ fontSize: 14 }} />}
          </CheckBoxCell>
          {COLUMNS.map((col, idx) => (
            <Fragment key={col.key}>
              {idx > 0 && <CellDivider />}
              <Cell width={col.width}>
                <HeaderText>{t(col.label)}</HeaderText>
              </Cell>
            </Fragment>
          ))}
        </TableHeader>

        {rows.length === 0 && (
          <TableRow style={{ justifyContent: 'center', minHeight: '120px', color: '#A6A6A6', fontSize: '14px' }}>
            {t('검색 결과가 없습니다.')}
          </TableRow>
        )}

        {rows.map(row => {
          const isChecked = selectedIds.includes(row.id);
          return (
            <TableRow key={row.id}>
              <CheckBoxCell checked={isChecked} clickable onClick={() => toggleSelectOne(row.id)}>
                {isChecked && <CheckIcon sx={{ fontSize: 14 }} />}
              </CheckBoxCell>
              <Cell width={160}>
                <DateText>{row.registeredDate?.replace('T', ' ')}</DateText>
              </Cell>
              <Cell width={400}>
                <NameText>{row.name}</NameText>
              </Cell>
              <Cell width={480}>
                {/* contentPreview 는 HTML 문자열이라 태그/엔티티를 벗겨 텍스트로 표시한다. */}
                <ContentText>{stripHtml(row.contentPreview)}</ContentText>
              </Cell>
              <Cell>
                <CategoryText>{categories.find(c => c.value === row.category)?.label || row.category}</CategoryText>
              </Cell>
              {/* <Cell>
                <RegisterText>{row.registeredId}</RegisterText>
              </Cell> */}
              <Cell>
                <UsageToggle
                  checked={!!row.isActive}
                  onChange={() => toggleMutation.mutate(row.id)}
                  disabled={toggleMutation.isPending}
                />
              </Cell>
              <Cell>
                <RowActionGroup>
                  <EditButton onClick={() => onEdit(row.id)}>{t('수정')}</EditButton>
                  <DeleteButton onClick={() => setDeleteTarget(row)}>{t('삭제')}</DeleteButton>
                </RowActionGroup>
              </Cell>
            </TableRow>
          );
        })}
      </Table>

      {/* 페이지네이션 */}
      <CommonPagination
        totalElements={data?.totalElements}
        limit={pageSize}
        page={page}
        onChange={(_, value) => setPage(value)}
      />

      {/* 로고&직인 등록 모달 */}
      <LogoSealModal
        open={isLogoSealModalOpen}
        branchId={branchId}
        logoSeal={logoSeal}
        onClose={() => setIsLogoSealModalOpen(false)}
      />

      {/* 삭제 확인 모달 */}
      <CommonDeleteModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
        title={t('해당 계약서를 삭제하시겠습니까?')}
        description={t('삭제한 계약서는 복구할 수 없습니다.')}
        isLoading={deleteMutation.isPending}
      />
    </>
  );
};

export default ElectronicContractManage;
